import { SyntheticEvent, useState } from 'react';
import { CustomButton, LoadingSpinner } from '@medtronic/pecc-react-component-library-js';
import { Box, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import { ProjectTabsEnum } from '~/api/types';
import { useGetProjectsInfiniteQuery } from '~/api/v2/hooks';
import { SearchTextField, Tooltip } from '~/components';
import { useDebounce, useInfiniteScroll, usePersistedTabValue } from '~/hooks';
import { useAppDispatch, useAppSelector } from '~/redux';
import { selectCurrentUser } from '~/redux/slices/currentUserSlice';
import { ModalTypesEnum, openModal } from '~/redux/slices/modalSlice';
import { PageTabsKeyEnum } from '~/redux/slices/pageTabsSlice';
import { getPriviligies } from '~/utils';
import { ProjectCard } from './ProjectDetailsPage/components';
import { loadingContainerStyles } from './ProjectDetailsPage/components/styles';

const projectTabs = [
  { [ProjectTabsEnum.MY_PROJECTS_TAB]: 'My Projects' },
  { [ProjectTabsEnum.MY_OU_TAB]: 'My operating unit' },
  { [ProjectTabsEnum.ALL_TAB]: 'All' },
  { [ProjectTabsEnum.ACTIVE_TAB]: 'Active' },
  { [ProjectTabsEnum.INACTIVE_TAB]: 'Inactive' },
];

const ProjectsPage = () => {
  const dispatch = useAppDispatch();
  const [searchText, setSearchText] = useState<string>('');
  const debouncedSearch = useDebounce(searchText);
  const [savedTab, setSavedTab] = usePersistedTabValue<ProjectTabsEnum>(
    PageTabsKeyEnum.PROJECTS,
    ProjectTabsEnum.MY_PROJECTS_TAB,
  );
  const { data, isSuccess, isFetching, fetchNextPage, hasNextPage } = useGetProjectsInfiniteQuery({
    tab: savedTab,
    search: debouncedSearch,
  });
  const user = useAppSelector(selectCurrentUser);

  const { ref } = useInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetching,
  });

  const canCreateProject = getPriviligies(user?.role?.id)?.project?.canCreate;

  const handleTabChange = (_: SyntheticEvent, newValue: ProjectTabsEnum) => {
    setSavedTab(newValue);
  };

  return (
    <Box>
      <Box marginRight="3rem">
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography variant="h3">
            Hi, <strong>{user?.first_name || ''}</strong>
          </Typography>
          <Tooltip
            title={
              !canCreateProject
                ? `This feature is unavailable for ${user ? `${user?.role.name} users` : 'your role'} . Please sign in or upgrade your access to add a new project`
                : null
            }
          >
            <div>
              <CustomButton
                id="new-project"
                onClick={() => {
                  dispatch(openModal({ type: ModalTypesEnum.CREATE_PROJECT }));
                }}
                label="New Project"
                disabled={!canCreateProject}
              />
            </div>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" marginTop="1rem">
          <Typography variant="body1">
            Welcome to the Healthcare Data Platform. Find a project below to begin.
          </Typography>
          <Box sx={{ borderRadius: '2rem' }}>
            <SearchTextField
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
              onClear={() => setSearchText('')}
            />
          </Box>
        </Stack>
      </Box>
      <Tabs value={savedTab} onChange={handleTabChange}>
        {projectTabs.map((tab) => (
          <Tab
            sx={{ textTransform: 'none' }}
            key={Object.keys(tab)[0]}
            label={Object.values(tab)[0]}
            value={Object.keys(tab)[0]}
          />
        ))}
      </Tabs>
      <Stack
        direction="row"
        spacing={6}
        justifyContent="start"
        useFlexGap
        flexWrap="wrap"
        mt="3rem"
        alignItems="stretch"
      >
        {isFetching && !data?.pages.length ? (
          <Box sx={{ width: '100%', ...loadingContainerStyles }}>
            <CircularProgress size={70} sx={{ color: '#1010eb' }} />
          </Box>
        ) : isSuccess ? (
          <>
            {data?.pages
              ?.flatMap((page) => page.data)
              ?.map((project) => (
                <ProjectCard key={`project-${project.id}`} project={project} />
              ))}

            {!data?.pages?.flatMap((page) => page.data)?.length && (
              <Typography width="100%" textAlign="center" variant="bodyDefaultStrong">
                No projects found.
              </Typography>
            )}

            {isFetching && hasNextPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <LoadingSpinner spinnerSize="s" />
              </Box>
            )}

            {hasNextPage && <Box ref={ref} sx={{ height: '1px' }} />}
          </>
        ) : null}
      </Stack>
    </Box>
  );
};

export default ProjectsPage;
